import React, { useId, useState, useEffect, forwardRef } from 'react'

import clsx from 'clsx'

import ApplyV2Picture from '@core/components/ApplyDialogV2/ApplyV2Picture'
import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import getCurrencyById from '@core/utils/helpers/getCurrencyById'

import './ApplyV2InputNumber.scss'

interface Props {
    value?: number | string | null
    label?: string
    onChange?: (value: number | null | string) => void
    onFocus?: React.FocusEventHandler<HTMLInputElement>
    onBlur?: React.FocusEventHandler<HTMLInputElement>
    isDisabled?: boolean
    className?: string
    id?: string | number
    name?: string
    isLoading?: boolean
    errorMessage?: string | string[] | null | boolean
    children?: React.ReactNode
    isRequired?: boolean
    placeholder?: string
    autoFocus?: boolean
    min?: number
    max?: number
    decimals?: number | null | undefined
    isShowErrorMessage?: boolean
    currencyId?: string | number
    description?: React.ReactNode
    numberFormat?: 'currency' | 'percent' | 'default'
}

const baseClass = 'ApplyV2InputNumber'

const ApplyV2InputNumber = forwardRef<HTMLInputElement, Props>(function ApplyV2InputNumber(
    {
        value,
        label,
        onChange,
        onFocus,
        onBlur,
        isDisabled = false,
        className = '',
        id,
        name,
        isLoading = false,
        errorMessage,
        isRequired = false,
        placeholder = '',
        autoFocus,
        decimals = null,
        isShowErrorMessage = true,
        currencyId,
        description,
        numberFormat = 'default',
    }: Props,
    ref
) {
    const defaultId = useId()
    const [isFocused, setIsFocused] = useState(false)
    const [displayValue, setDisplayValue] = useState(value ? formatWithSpaces(String(value)) : '')

    useEffect(() => {
        if (!isFocused) {
            setDisplayValue(value !== null && value !== undefined ? formatWithSpaces(String(value)) : '')
        }
    }, [value, isFocused])

    const getStepValue = () => {
        if (decimals === null || decimals === undefined) return 1
        if (decimals === 0) return 1
        return 1 / Math.pow(10, decimals)
    }

    function formatWithSpaces(val: string) {
        if (!val) return ''
        const [intPart, fracPart] = val.split('.')
        const cleanInt = intPart.replace(/\s/g, '')
        const spacedInt = cleanInt.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        return fracPart !== undefined ? `${spacedInt}.${fracPart}` : spacedInt
    }

    const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
        const input = e.currentTarget
        const selectionStart = input.selectionStart ?? 0

        let val = input.value.replace(/,/g, '.')
        val = val.replace(/[^\d.]/g, '')

        const [intPart, fracPart] = val.split('.')
        const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        const formatted =
            fracPart !== undefined
                ? formattedInt +
                '.' +
                (decimals !== null && decimals !== undefined ? fracPart.slice(0, decimals) : fracPart)
                : formattedInt

        const diff = formatted.length - input.value.length
        setDisplayValue(formatted)
        onChange?.(val || null)

        requestAnimationFrame(() => {
            const pos = Math.max(0, selectionStart + diff)
            input.setSelectionRange(pos, pos)
        })
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false)
        let val = (value ?? '').toString().replace(/\s/g, '').replace(/,/g, '.')
        if (val === '' || isNaN(Number(val))) {
            setDisplayValue('')
            onChange?.(null)
            onBlur?.(e)
            return
        }

        if (decimals === 0) {
            val = val.split('.')[0]
            setDisplayValue(formatWithSpaces(val))
            onChange?.(val)
        } else if (decimals !== null && decimals !== undefined && decimals > 0) {
            const fixed = Number(val).toFixed(decimals)
            setDisplayValue(formatWithSpaces(fixed))
            onChange?.(fixed)
        } else {
            setDisplayValue(formatWithSpaces(val))
            onChange?.(Number(val))
        }

        onBlur?.(e)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (['+', 'e', 'E'].includes(e.key)) e.preventDefault()
        const input = e.currentTarget

        if (decimals === 0 && ['.', ','].includes(e.key)) {
            e.preventDefault()
        }

        if (['.', ','].includes(e.key) && input.value.includes('.') && (decimals === 0 || (decimals && decimals > 0))) {
            e.preventDefault()
        }
    }

    const currencyRes = getCurrencyById(currencyId)

    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--error`]: !!errorMessage,
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--loading`]: isLoading,
            })}
            data-error={!!errorMessage ? 'true' : 'false'}
        >
            {label && (
                <label className={`${baseClass}__label`} htmlFor={id ? id.toString() : defaultId}>
                    {label}
                    {isRequired && '*'}
                </label>
            )}
            <div
                className={clsx(`${baseClass}__input-wrapper`, {
                    [`${baseClass}__input-wrapper--error`]: !!errorMessage,
                    [`${baseClass}__input-wrapper--disabled`]: isDisabled,
                    [`${baseClass}__input-wrapper--loading`]: isLoading,
                })}
            >
                {currencyRes && (
                    <div className={`${baseClass}__input--currencies`}>
                        <span>{currencyRes.short}</span>
                        <ApplyV2Picture
                            className={`${baseClass}__input-currency`}
                            fallbackSrc={`${window.location.protocol}//${window.location.hostname}/${currencyRes.img}`}
                            alt={currencyRes.short ?? ''}
                        />
                    </div>
                )}

                {numberFormat === 'percent' && (
                    <div className={`${baseClass}__input-percent`}>
                        <i className="fa-light fa-percent"></i>
                    </div>
                )}

                <input
                    ref={ref}
                    type="text"
                    inputMode={(decimals ?? 0) > 0 ? 'decimal' : 'numeric'}
                    step={getStepValue()}
                    id={id ? id.toString() : defaultId}
                    name={name}
                    value={displayValue}
                    onKeyDown={handleKeyDown}
                    onInput={handleInput}
                    onFocus={(e) => {
                        setIsFocused(true)
                        onFocus?.(e)
                    }}
                    onBlur={handleBlur}
                    disabled={isDisabled}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    className={clsx(`${baseClass}__input`, {
                        [`${baseClass}__input__currency`]: !!currencyRes,
                        [`${baseClass}__input--error`]: !!errorMessage,
                        [`${baseClass}__input--loading`]: isLoading,
                    })}
                />
            </div>

            {description && <div className={`${baseClass}__description`}>{description}</div>}

            {errorMessage && isShowErrorMessage && (
                <div className={`${baseClass}__error`}>
                    <div className={`${baseClass}__error--icon`}>
                        <WarningErrorIcon />
                    </div>
                    <div className={`${baseClass}__error--text`}>{errorMessage}</div>
                </div>
            )}
        </div>
    )
})

export default ApplyV2InputNumber
